import { useDispatch, useSelector } from "react-redux";
import { CustomizationProvider } from "@twilio-paste/core/customization";
import { useEffect } from "react";

import { RootContainer } from "./RootContainer";
import { AppState, EngagementPhase } from "../store/definitions";
import { sessionDataHandler } from "../sessionDataHandler";
import { initSession } from "../store/actions/initActions";
import { AppDispatch } from "../store/store";
import { addNotification, changeEngagementPhase } from "../store/actions/genericActions";
import { notifications } from "../notifications";
import { reportClientError } from "../utils/reportClientError";
import { extrairCodigos } from "../utils/diagnosticoDeEnvio";
import { descreverErro } from "../utils/descreverErro";

export function WebchatWidget() {
    const theme = useSelector((state: AppState) => state.config.theme);
    const dispatch = useDispatch<AppDispatch>();

    useEffect(() => {
        const data = sessionDataHandler.tryResumeExistingSession();
        if (!data) {
            // sem token guardado → formulário
            dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
            return;
        }
        /*
         * `initSession` é um thunk ASSÍNCRONO: a falha pode chegar como promise rejeitada ou como
         * `{ ok: false }` depois de já ter sido tratada pelo thunk. Daí o `await` e a leitura do
         * desfecho antes de considerar a sessão salva válida.
         */
        const retomarSessao = async () => {
            try {
                const resultado = await dispatch(
                    initSession({ token: data.token, conversationSid: data.conversationSid })
                );
                /*
                 * `?.` de propósito: um retorno inesperadamente vazio faria `resultado.ok` lançar
                 * TypeError DENTRO do try, e o catch abaixo despacharia uma SEGUNDA notificação em
                 * cima da que o `initSession` já mostrou — dois avisos empilhados na tela.
                 */
                if (resultado?.ok !== true) {
                    sessionDataHandler.clear();
                }
            } catch (e) {
                /*
                 * A sessão guardada é descartada de propósito: se o token expirou/foi recusado,
                 * mantê-la faria o próximo load tentar o MESMO token podre e falhar igual.
                 */
                sessionDataHandler.clear();
                // motivo técnico só na telemetria — a tela do apostador leva mensagem genérica
                /*
                 * `extrairCodigos` separa erro DO FORNECEDOR de erro NOSSO: o objeto que o SDK
                 * lança traz `errorCode`/`httpStatusCode`, e sem eles o alerta chega mudo (medido
                 * na produção de 04-06/09/2026). Campo estruturado, não texto — a `message` do
                 * fornecedor continua fora.
                 */
                reportClientError("session_init_failed", { reason: descreverErro(e), ...extrairCodigos(e) });
                dispatch(addNotification(notifications.failedToInitSessionNotification()));
                dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
            }
        };
        void retomarSessao();
    }, [dispatch]);

    return (
        <CustomizationProvider
            baseTheme={theme?.isLight ? "default" : "dark"}
            theme={theme?.overrides}
            elements={{
                MESSAGE_INPUT: {
                    boxShadow: "none!important" as "none"
                },
                MESSAGE_INPUT_BOX: {
                    display: "inline-block",
                    boxShadow: "none"
                },
                ALERT: {
                    paddingTop: "space30",
                    paddingBottom: "space30"
                },
                BUTTON: {
                    "&[aria-disabled='true'][color='colorTextLink']": {
                        color: "colorTextLinkWeak"
                    }
                }
            }}
            style={{ minHeight: "100%", minWidth: "100%" }}
        >
            <RootContainer />
        </CustomizationProvider>
    );
}
